import { useState } from "react";
import VoiceNoteButton from "./VoiceNoteButton";

// 16px Schrift verhindert das automatische Hineinzoomen von iOS Safari beim Fokus.
const inputCls = (missing) =>
  `w-full h-11 px-3 rounded-lg border bg-zinc-900 text-zinc-100 text-base placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500/60 transition-colors ${
    missing ? "border-red-500/60" : "border-zinc-700"
  }`;

function SelectField({ field, value, onChange, missing }) {
  const opts = field.options || [];
  const isOtherValue = !!value && !opts.includes(value);
  const [otherMode, setOtherMode] = useState(isOtherValue);
  const showOther = field.allow_other && (otherMode || isOtherValue);
  const selectVal = showOther ? "Sonstiges" : (value ?? "");
  return (
    <div className="space-y-2">
      <div className="relative">
        <select
          className={`${inputCls(missing)} appearance-none pr-10`}
          value={selectVal}
          onChange={(e) => {
            const v = e.target.value;
            if (field.allow_other && v === "Sonstiges") { setOtherMode(true); onChange(null); return; }
            setOtherMode(false);
            onChange(v || null);
          }}
          data-testid={`vcheck-f-${field.key}`}
        >
          <option value="">— bitte wählen —</option>
          {opts.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
      {showOther && (
        <input type="text" className={inputCls(missing)} value={isOtherValue ? value : ""}
               onChange={(e) => onChange(e.target.value || null)}
               placeholder="Bitte angeben …" autoFocus={otherMode && !isOtherValue}
               data-testid={`vcheck-f-${field.key}-other`} />
      )}
    </div>
  );
}

export default function VCheckField({ field, value, onChange, missing, projectId, onVoiceApplied }) {
  const baseCls = inputCls(missing);

  if (field.type === "text") {
    return <input type="text" className={baseCls} value={value ?? ""}
                  onChange={(e) => onChange(e.target.value)}
                  placeholder={field.hint || ""} readOnly={field.readonly}
                  data-testid={`vcheck-f-${field.key}`} />;
  }
  if (field.type === "number") {
    return (
      <div className="relative">
        <input type="number" step="any" inputMode="decimal" className={`${baseCls} pr-14`} value={value ?? ""}
               onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
               placeholder={field.hint || ""}
               data-testid={`vcheck-f-${field.key}`} />
        {field.unit && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[13px] text-zinc-400 pointer-events-none">{field.unit}</span>}
      </div>
    );
  }
  if (field.type === "date") {
    return <input type="date" className={`${baseCls} appearance-none`} value={value ?? ""}
                  onChange={(e) => onChange(e.target.value)}
                  data-testid={`vcheck-f-${field.key}`} />;
  }
  if (field.type === "textarea") {
    return (
      <div className="space-y-1.5">
        <textarea rows={field.readonly ? 5 : 3} className={`${baseCls} h-auto py-2 resize-y`} value={value ?? field.default ?? ""}
                  onChange={(e) => onChange(e.target.value)}
                  placeholder={field.hint || ""} readOnly={field.readonly}
                  data-testid={`vcheck-f-${field.key}`} />
        {!field.readonly && projectId && (
          <VoiceNoteButton projectId={projectId} fieldKey={field.key}
                           onApplied={onVoiceApplied} />
        )}
      </div>
    );
  }
  if (field.type === "bool") {
    return (
      <div className="flex gap-2" data-testid={`vcheck-f-${field.key}`}>
        {[["Ja", true], ["Nein", false]].map(([lbl, val]) => (
          <button key={lbl} type="button" onClick={() => onChange(val)}
                  className={`flex-1 h-11 px-3 rounded-lg border text-[14px] font-semibold transition-colors ${
                    value === val ? "bg-emerald-500 border-emerald-500 text-zinc-950"
                    : `bg-zinc-900 text-zinc-300 hover:bg-zinc-800 ${missing ? "border-red-500/60" : "border-zinc-700"}`
                  }`}
                  data-testid={`vcheck-f-${field.key}-${String(val)}`}>{lbl}</button>
        ))}
      </div>
    );
  }
  if (field.type === "select") {
    return <SelectField field={field} value={value} onChange={onChange} missing={missing} />;
  }
  if (field.type === "multiselect") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (o) => arr.includes(o) ? onChange(arr.filter((x) => x !== o)) : onChange([...arr, o]);
    return (
      <div className="flex flex-wrap gap-2" data-testid={`vcheck-f-${field.key}`}>
        {(field.options || []).map((o) => (
          <button key={o} type="button" onClick={() => toggle(o)}
                  className={`h-10 px-3 rounded-lg text-[13.5px] font-medium border transition-colors ${
                    arr.includes(o) ? "bg-emerald-500 border-emerald-500 text-zinc-950"
                    : `bg-zinc-900 text-zinc-300 hover:bg-zinc-800 ${missing ? "border-red-500/50" : "border-zinc-700"}`
                  }`}>{o}</button>
        ))}
      </div>
    );
  }
  return null;
}
